import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type ReactNode, useEffect, useState } from 'react';
import { IntlProvider, useIntl } from 'react-intl-next';

type LoadI18nMessagesResult = {
	locale: string;
	messages: Record<string, string> | undefined;
};

export type I18nMessageLoader = (
	messageTemplateId: string,
	placementId: string,
	locale: string,
) => Promise<{ default: Record<string, string> }>;

export const loadPlacementMessages = async (
	messageTemplateId: string,
	placementId: string,
	_locale: string,
): Promise<{ default: Record<string, string> }> => {
	const candidate = `@post-office/i18n-translation/${messageTemplateId}-in-app/${placementId}`;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return await import(candidate);
};

/**
 * This react hook loads the messages for a given locale from the NPM package.
 * It returns the locale and the messages.
 * @param messageTemplate
 * @param placement
 * @param messageLoader
 * @returns {locale: string, messages: Record<string, string> | undefined}
 */
export function useLoadI18nMessages(
	messageTemplateId: string,
	placementId: string,
	messageLoader: I18nMessageLoader = loadPlacementMessages,
): LoadI18nMessagesResult {
	const [messages, setMessages] = useState<Record<string, string> | undefined>();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	let locale = 'en';
	try {
		locale = useIntl().locale;
	} catch {
		// fallback to en
	}

	useEffect(() => {
		messageLoader(messageTemplateId, placementId, locale)
			.then((loadedMessages) => {
				setMessages(loadedMessages.default);
			})
			.catch(() => {
				setMessages({});
				fireAnalyticsEvent({
					action: 'error',
					actionSubject: 'i18nMessageLoader',
					eventType: 'operational',
				});
			});
	}, [fireAnalyticsEvent, locale, messageLoader, messageTemplateId, placementId]);

	return { locale, messages };
}

type I18nProviderProps = {
	children: ReactNode;
	messageTemplateId: string;
	placementId: string;
	messageLoader?: I18nMessageLoader;
};

const I18nProvider = ({
	children,
	messageTemplateId,
	placementId,
	messageLoader,
}: I18nProviderProps): JSX.Element => {
	const { locale, messages } = useLoadI18nMessages(messageTemplateId, placementId, messageLoader);
	if (!messages || Object.keys(messages).length === 0) {
		return <>{children}</>;
	}
	return (
		<IntlProvider locale={locale} messages={messages}>
			{children}
		</IntlProvider>
	);
};

type MaybeI18nProviderProps = {
	isI18nMigrationInAppEnabled: boolean;
	messageTemplateId: string;
	placementId: string | undefined;
	children: ReactNode;
	messageLoader?: I18nMessageLoader;
};

export const MaybeI18nProvider = ({
	isI18nMigrationInAppEnabled,
	messageTemplateId,
	placementId,
	children,
	messageLoader = loadPlacementMessages,
}: MaybeI18nProviderProps): JSX.Element => {
	if (!isI18nMigrationInAppEnabled || !placementId) {
		return <>{children}</>;
	}
	return (
		<I18nProvider
			messageTemplateId={messageTemplateId}
			placementId={placementId}
			messageLoader={messageLoader}
		>
			{children}
		</I18nProvider>
	);
};
